import type { IntlShape } from 'react-intl';

import { formatTimeframeToParts } from '../../../../../formatters/timeframe/timeframe-formatter.js';
import type {
  DateTimeFormatter,
  FormatterWithPrecision,
  TimeFrameFormatter,
} from '../../../../core/types/formatter.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { XYCHART_MESSAGES } from '../../../messages/messages.js';
import type {
  AxisIdToFormatter,
  XYChartAxisInternal,
} from '../../../types/xy-chart-internals.js';
import { TIME_SCALE_TYPE } from '../../../types/xy-chart.js';

export type TooltipFormatter = FormatterWithPrecision | TimeFrameFormatter;

export function getTooltipFormatter(
  axisConfig: Pick<XYChartAxisInternal, 'id' | 'type' | 'formatter'>,
  axisIdToFormatter: AxisIdToFormatter,
  intl: IntlShape,
): TooltipFormatter {
  const separator = intl.formatMessage(XYCHART_MESSAGES.separatorDash);
  const getFormatter = () => safeGet(axisIdToFormatter, axisConfig.id);

  const formatTimeValue = (from: Date, to?: Date) => {
    if (to) {
      const timeframeParts = formatTimeframeToParts(
        from,
        to,
        intl,
        true,
        XYCHART_MESSAGES.separatorDash,
      );
      return typeof timeframeParts === 'string'
        ? timeframeParts
        : timeframeParts.join(' ');
    }
    return formatTimeframeToParts(from, from, intl)[0];
  };

  if (axisConfig.type === TIME_SCALE_TYPE) {
    //Default Formatter
    if (!axisConfig.formatter) {
      return formatTimeValue;
    }
    //Custom Formatter
    return (from: Date, to?: Date) => {
      const formatter = getFormatter() as DateTimeFormatter;
      if (to) {
        return `${formatter(from)} ${separator} ${formatter(to)}`;
      }
      return formatter(from);
    };
  }
  //Any non-time formatter
  return getFormatter();
}
